import React, { ReactNode, useEffect } from "react";
import { animated, useSpring } from "react-spring";
import { Box, useMediaQuery } from "@mui/material";
import useResponsive from "@/hooks/useResponsive";
import { getSlideStyles, slideCardStyles } from "./styles";

const AnimatedContainer = animated(Box);

interface SlideProps {
  offsetRadius: number;
  index: number;
  children?: ReactNode;
  activeIndex: number;
}

const Slide: React.FC<SlideProps> = ({
  offsetRadius,
  index,
  children,
  activeIndex,
}) => {
  const isSmallMobile = useMediaQuery("(max-width: 374.98px)");
  const isMobile = useResponsive("down", "md");
  const isTablet = useResponsive("between", "md", "lg");
  const isLargeDesktop = useResponsive("up", "xl");

  const translateY = -104;

  // Animation transform - set up
  const [{ transform, top, opacity }, api] = useSpring(() => ({
    transform: `translateX(0%) translateY(${translateY}%) scale(${1})`,
    top: `100%`,
    opacity: index === 0 ? 1 : 0,
  }));

  // Animation start on index change
  useEffect(() => {
    const offsetFromMiddle = index - offsetRadius;
    const totalPresentables = 2 * offsetRadius + 1;

    if (activeIndex === 0) {
      api.start({
        transform: `translateX(0%) translateY(${translateY}%) scale(${1})`,
        top: `100%`,
        opacity: index === 2 ? 1 : 0,
      });
    }

    if (activeIndex === 1) {
      let translateY = -50;

      if (index === 0) {
        // translate first slide
        translateY = isMobile ? 33 : isTablet ? 35 : isLargeDesktop ? 38 : 42;
      } else if (index === totalPresentables - 1) {
        // translate third slide
        translateY = isMobile
          ? -108
          : isTablet
          ? -100
          : isLargeDesktop
          ? -100
          : -104;
      }
      api.start({
        transform: `translateX(0%) translateY(${translateY}%) scale(${
          index === 0 ? 1 : index === 1 ? 0.95 : 1
        })`,
        top: `${
          offsetRadius === 0 ? 50 : 50 + (offsetFromMiddle * 50) / offsetRadius
        }%`,
        opacity: index === 0 ? 0 : index === 1 ? 1 : 1,
      });
    }

    if (activeIndex === 2) {
      let translateY = -50;
      if (index === 0) {
        // translate first slide
        translateY = isMobile ? 8 : isTablet ? 0 : isLargeDesktop ? 0 : 4;
      } else if (index === totalPresentables - 1) {
        // translate third slide
        translateY = isMobile
          ? -108
          : isTablet
          ? -100
          : isLargeDesktop
          ? -100
          : -104;
      }

      api.start({
        transform: `translateX(0%) translateY(${translateY}%) scale(${
          index === 0 ? 0.9 : index === 1 ? 0.95 : 1
        })`,
        top: `${
          offsetRadius === 0 ? 50 : 50 + (offsetFromMiddle * 50) / offsetRadius
        }%`,
        opacity: 1,
      });
    }
  }, [
    activeIndex,
    api,
    index,
    isLargeDesktop,
    isMobile,
    isTablet,
    offsetRadius,
    translateY,
  ]);

  return (
    <AnimatedContainer
      style={{
        top,
        transform,
        opacity,
        zIndex: index === 0 ? 1 : index === 1 ? 2 : 3,
      }}
      sx={getSlideStyles(isSmallMobile)}
    >
      <Box sx={slideCardStyles}>{children}</Box>
    </AnimatedContainer>
  );
};

export default Slide;
